import React, { ReactNode, createContext, useEffect } from "react";
import { brandingConfig, BrandingConfig } from "./branding.config";
import { WHITELABEL_KEY, USE_WHITELABEL_BRANDING } from "../globals";
import { useAtomValue } from "jotai";
import { darkModeAtom } from "../jotai/atoms";
export const BrandingContext = createContext<BrandingConfig>({} as BrandingConfig);

interface BrandingProviderProps {
  children: ReactNode;
}

const BrandingProvider: React.FC<BrandingProviderProps> = ({ children }) => {
  const darkMode = useAtomValue(darkModeAtom);
  const branding: BrandingConfig = USE_WHITELABEL_BRANDING
    ? brandingConfig[WHITELABEL_KEY] || brandingConfig["default"]
    : brandingConfig["default"];

  useEffect(() => {
    // Favicon and installed app options
    const elementsToUpdate: HTMLElement[] = [];

    const icon =
      (document.querySelector("link[rel='icon']") as HTMLLinkElement) ||
      document.createElement("link");
    icon.type = "image/svg+xml";
    icon.rel = "icon";
    icon.href = `${branding.faviconPrefix}/favicon.svg`;
    elementsToUpdate.push(icon);

    const shortcutIcon =
      (document.querySelector("link[rel='shortcut icon']") as HTMLLinkElement) ||
      document.createElement("link");
    shortcutIcon.rel = "shortcuticon";
    shortcutIcon.href = `${branding.faviconPrefix}/favicon.ico`;
    elementsToUpdate.push(shortcutIcon);

    const appleTouchIcon =
      (document.querySelector("link[rel='apple-touch-icon']") as HTMLLinkElement) ||
      document.createElement("link");
    appleTouchIcon.rel = "apple-touch-icon";
    appleTouchIcon.href = `${branding.faviconPrefix}/apple-touch-icon.png`;
    elementsToUpdate.push(appleTouchIcon);

    const appleMobileWebAppTitle =
      (document.querySelector("meta[name='apple-mobile-web-app-title']") as HTMLMetaElement) ||
      document.createElement("meta");
    appleMobileWebAppTitle.name = "apple-mobile-web-app-title";
    appleMobileWebAppTitle.content = branding.title;
    elementsToUpdate.push(appleMobileWebAppTitle);

    const manifest =
      (document.querySelector("link[rel='manifest']") as HTMLLinkElement) ||
      document.createElement("link");
    manifest.rel = "manifest";
    manifest.href = `${branding.faviconPrefix}/site.webmanifest`;
    elementsToUpdate.push(manifest);

    const title =
      (document.querySelector("title") as HTMLTitleElement) || document.createElement("title");
    title.textContent = branding.title;
    elementsToUpdate.push(title);

    for (const element of elementsToUpdate) {
      if (!element.parentNode) {
        document.head.appendChild(element);
      }
    }

    return () => {};
  }, [branding, darkMode]);

  return <BrandingContext.Provider value={branding}>{children}</BrandingContext.Provider>;
};

export default BrandingProvider;
